<div class="extraction-review-container">

  <!-- Header -->
  <div class="header">
    <button class="btn-back" (click)="goBack()">← {{ 'extractionReview.back' | translate }}</button>
    <h2 class="page-title">
      {{ 'extractionReview.title' | translate }}
    </h2>
    <div class="doc-nav" *ngIf="totalDocuments > 0">
      <span class="doc-nav-label">Doc {{ documentNumber }} / {{ totalDocuments }}</span>
      <button class="btn-nav" [disabled]="!hasPrevDocument" (click)="goToPrevDocument()" title="Previous document" aria-label="Previous document">▲</button>
      <button class="btn-nav" [disabled]="!hasNextDocument" (click)="goToNextDocument()" title="Next document" aria-label="Next document">▼</button>
    </div>
  </div>

  <!-- Per-file signature badges — a batch/ZIP upload only; a single-file
       upload's badge is shown on the dossier page instead. -->
  <div class="file-signatures-row" *ngIf="fileSignatures.length > 0">
    <span class="file-signatures-label">{{ 'extractionReview.signatures' | translate }}</span>
    <span class="file-signature-item" *ngFor="let f of fileSignatures">
      <span class="file-signature-name" [title]="f.filename">{{ f.filename }}</span>
      <app-signature-badge
        [preloaded]="f"
        (open)="openSignatureDialog(f.filename, $event)"
      ></app-signature-badge>
    </span>
  </div>

  <!-- Error -->
  <div class="error-message" *ngIf="error && !needsDekUnlock">{{ error }}</div>

  <!-- DEK unlock prompt -->
  <div class="dek-unlock-section" *ngIf="needsDekUnlock && !dekUnlocked">
    <div class="dek-unlock-card">
      <div class="dek-icon">🔐</div>
      <h3>{{ 'dossierDetail.dekUnlockTitle' | translate }}</h3>
      <p class="dek-hint">{{ 'dossierDetail.dekUnlockHint' | translate }}</p>
      <div class="dek-form">
        <input
          type="password"
          [(ngModel)]="dekPassword"
          [placeholder]="'dossierDetail.dekPasswordPlaceholder' | translate"
          (keyup.enter)="unlockDek()"
          [disabled]="dekUnlocking"
          class="dek-password-input"
          aria-label="Private key password"
        />
        <button
          class="btn-primary dek-unlock-btn"
          (click)="unlockDek()"
          [disabled]="dekUnlocking || !dekPassword"
        >
          <span *ngIf="!dekUnlocking">{{ 'dossierDetail.dekUnlockButton' | translate }}</span>
          <span *ngIf="dekUnlocking">{{ 'dossierDetail.dekUnlocking' | translate }}</span>
        </button>
      </div>
      <div class="dek-error" *ngIf="dekError">
        {{ dekError }}
        <button *ngIf="noKeypairFound" class="btn-link" (click)="openMyAccountSecurity()">
          {{ 'dossierDetail.openMyAccount' | translate }}
        </button>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div class="loading" *ngIf="loading && !needsDekUnlock">{{ 'dossier.loading' | translate }}</div>

  <!-- Waiting / re-extracting -->
  <div class="waiting-banner" *ngIf="!loading && (isWaiting || reExtracting)">
    <div class="waiting-spinner"></div>
    <span *ngIf="reExtracting">{{ 'extractionReview.reExtracting' | translate }}</span>
    <span *ngIf="!reExtracting">{{ 'extractionReview.waiting' | translate }}</span>
  </div>

  <!-- Re-extract controls -->
  <div class="re-extract-bar" *ngIf="!loading && (isExtracted || isExtractionFailed)">
    <!-- Backend selector (developer only) -->
    <ng-container *ngIf="isDeveloper">
      <label class="control-label">{{ 'extractionReview.backend' | translate }}</label>
      <select class="control-select"
              [(ngModel)]="selectedBackend"
              (ngModelChange)="onBackendChange()"
              [disabled]="reExtracting">
        <option value="openai">OpenAI</option>
        <option value="ollama">Local AI</option>
        <option value="bedrock">Bedrock</option>
        <option value="local">vLLM</option>
      </select>

      <!-- Model selector (filtered by backend, free-text editable) -->
      <label class="control-label">{{ 'extractionReview.model' | translate }}</label>
      <input class="control-select"
             list="extraction-model-options"
             [(ngModel)]="selectedModel"
             [disabled]="reExtracting"
             placeholder="Select or type model name">
      <datalist id="extraction-model-options">
        <option *ngFor="let m of filteredModels" [value]="m">{{ displayModel(m) }}</option>
      </datalist>
    </ng-container>

    <!-- Model tier slider for non-developer users -->
    <ng-container *ngIf="!isDeveloper && modelTiers.length > 0">
      <app-model-tier-slider
        [tiers]="modelTiers"
        [selectedModel]="selectedModel"
        (modelChange)="onTierModelChange($event)">
      </app-model-tier-slider>
    </ng-container>

    <!-- Schema source toggle -->
    <label class="control-label">{{ 'extractionReview.schemaSource' | translate }}</label>
    <select class="control-select schema-select"
            [ngModel]="schemaSource"
            (ngModelChange)="onSchemaSourceChange($event)"
            [disabled]="reExtracting">
      <option value="internal">{{ 'schemaSourceOptions.internal' | translate }}</option>
      <option value="external">{{ 'schemaSourceOptions.external' | translate }}</option>
    </select>

    <!-- Document type selector -->
    <label class="control-label">{{ 'extractionReview.docType' | translate }}</label>
    <select class="control-select"
            [(ngModel)]="selectedDocumentType"
            (ngModelChange)="onDocumentTypeChange()"
            [disabled]="reExtracting">
      <option *ngFor="let t of activeDocumentTypes" [value]="t">{{ t }}</option>
    </select>

    <!-- Output format selector -->
    <label class="control-label">{{ 'extractionReview.outputFormat' | translate }}</label>
    <select class="control-select"
            [(ngModel)]="selectedOutputFormat"
            [disabled]="reExtracting || schemaSource === 'external'">
      <option *ngFor="let f of activeOutputFormats" [value]="f">{{ f | uppercase }}</option>
    </select>

    <!-- Re-extract button -->
    <button class="btn-reextract" [disabled]="reExtracting" (click)="reExtract()">
      {{ reExtracting ? ('actions.processing' | translate) : ('\u2699 ' + ('extractionReview.reExtract' | translate)) }}
    </button>

    <!-- Mark Reviewed button -->
    <button
      class="btn-reviewed"
      [disabled]="!isExtracted || markingReviewed"
      (click)="markReviewed()"
    >
      {{ markingReviewed ? ('actions.processing' | translate) : ('\u2713 Reviewed') }}
    </button>
  </div>

  <!-- Main content: side-by-side -->
  <div class="split-layout" *ngIf="!loading && isExtracted">

    <!-- LEFT: Extraction result (60%) -->
    <div class="panel-left">

      <!-- No data message -->
      <div class="no-result" *ngIf="hasNoData">
        {{ 'extractionReview.noResult' | translate }}
      </div>

      <!-- Rich rendering when data exists -->
      <ng-container *ngIf="!hasNoData">

        <!-- View toggle buttons -->
        <div class="view-toggle">
          <button class="btn-toggle" [class.active]="viewMode === ViewMode.TREE"
                  (click)="viewMode = ViewMode.TREE" *ngIf="!isTableResult && !isCsvResult">
            🌳 {{ 'extract.treeView' | translate }}
          </button>
          <button *ngIf="!isXmlResult && !isTableResult && !isCsvResult" class="btn-toggle"
                  [class.active]="viewMode === ViewMode.JSON" (click)="viewMode = ViewMode.JSON">
            &lt;/&gt; JSON
          </button>
          <button *ngIf="isXmlResult && !isTableResult" class="btn-toggle"
                  [class.active]="viewMode !== ViewMode.TREE" (click)="viewMode = ViewMode.JSON">
            &lt;/&gt; XML
          </button>
          <button *ngIf="isTableResult" class="btn-toggle active">
            📊 {{ tableFormat | uppercase }}
          </button>
          <!-- CSV result buttons -->
          <ng-container *ngIf="isCsvResult">
            <button class="btn-toggle" [class.active]="viewMode === ViewMode.SPREADSHEET" (click)="viewMode = ViewMode.SPREADSHEET">📊 Spreadsheet</button>
            <button class="btn-toggle" [class.active]="viewMode === ViewMode.JSON" (click)="viewMode = ViewMode.JSON">CSV</button>
          </ng-container>
          <button class="btn-toggle" (click)="downloadResult()">
            ⬇ Download {{ downloadLabel }}
          </button>
          <button class="btn-toggle" *ngIf="pages.length > 0" (click)="downloadOcrText()">
            ⬇ {{ 'ocr.downloadText' | translate }}
          </button>
          <button
            class="btn-save-inline"
            [disabled]="!isDirty || saving"
            (click)="saveAll()"
            title="Save manual edits"
          >
            {{ saving ? ('actions.processing' | translate) : ('extractionReview.save' | translate) }}
          </button>
        </div>

        <!-- JSON Tree View -->
        <div *ngIf="viewMode === ViewMode.TREE && !isXmlResult && !isCsvResult" class="tree-view">
          <div class="tree-container">
            <div class="tree-item">
              <div class="tree-node-display">
                <span class="tree-key">Document Data</span>
                <span class="tree-type">object</span>
              </div>
              <div class="tree-children">
                <ng-container *ngFor="let key of getVisibleKeys(effectiveData)">
                  <ng-container *ngTemplateOutlet="jsonNodeTpl; context: { key: key, value: $any(effectiveData)[key], path: key }"></ng-container>
                </ng-container>
              </div>
            </div>
          </div>
        </div>

        <!-- Recursive JSON tree node template -->
        <ng-template #jsonNodeTpl let-key="key" let-value="value" let-path="path">
          <div class="tree-item">
            <!-- ARRAY -->
            <ng-container *ngIf="isArray(value); else jsonObject">
              <div class="tree-node-display">
                <span class="tree-key">{{ key }}</span>
                <span class="tree-type">array [{{ value.length }}]</span>
              </div>
              <div class="tree-children">
                <ng-container *ngFor="let item of value; let i = index">
                  <ng-container *ngTemplateOutlet="jsonNodeTpl; context: { key: '[' + i + ']', value: item, path: path + '.' + i }"></ng-container>
                </ng-container>
              </div>
            </ng-container>

            <!-- OBJECT -->
            <ng-template #jsonObject>
              <ng-container *ngIf="isObject(value); else jsonScalar">
                <!-- Value+confidence pair → collapse inline -->
                <ng-container *ngIf="asValueConfidencePair(value) as pair; else jsonObjectExpanded">
                  <div class="tree-node-display">
                    <span class="tree-key">{{ key }}</span>
                    <ng-container *ngIf="editingPath === path + '.value'; else pairDisplay">
                      <input class="tree-edit-input" [(ngModel)]="editingValue"
                             (blur)="commitEdit(path + '.value', value['value'])"
                             (keydown)="onEditKeydown($event, path + '.value', value['value'])"
                             autofocus />
                    </ng-container>
                    <ng-template #pairDisplay>
                      <span class="tree-value" (click)="startEdit(path + '.value', pair.value)" (mouseenter)="onValueHover(pair.value)" (mouseleave)="onValueHoverOut()">{{ pair.value }}</span>
                      <span *ngIf="pair.confidence !== null"
                            [class]="confidenceClass(pair.confidence)">{{ confidencePercent(pair.confidence) }}</span>
                    </ng-template>
                  </div>
                </ng-container>
                <!-- Regular object → expand children -->
                <ng-template #jsonObjectExpanded>
                  <div class="tree-node-display">
                    <span class="tree-key">{{ key }}</span>
                    <span class="tree-type">object</span>
                  </div>
                  <div class="tree-children">
                    <ng-container *ngFor="let subKey of getObjectKeys(value)">
                      <ng-container *ngTemplateOutlet="jsonNodeTpl; context: { key: subKey, value: value[subKey], path: path + '.' + subKey }"></ng-container>
                    </ng-container>
                  </div>
                </ng-template>
              </ng-container>

              <!-- SCALAR -->
              <ng-template #jsonScalar>
                <div class="tree-node-display">
                  <span class="tree-key">{{ key }}</span>
                  <ng-container *ngIf="editingPath === path; else scalarDisplay">
                    <input class="tree-edit-input" [(ngModel)]="editingValue"
                           (blur)="commitEdit(path, value)"
                           (keydown)="onEditKeydown($event, path, value)"
                           autofocus />
                  </ng-container>
                  <ng-template #scalarDisplay>
                    <span class="tree-value" (click)="startEdit(path, value)" (mouseenter)="onValueHover(formatValue(value))" (mouseleave)="onValueHoverOut()">{{ formatValue(value) }}</span>
                  </ng-template>
                </div>
              </ng-template>
            </ng-template>
          </div>
        </ng-template>

        <!-- XML Tree View -->
        <div *ngIf="viewMode === ViewMode.TREE && isXmlResult && xmlContent" class="tree-view">
          <div class="tree-container" *ngIf="parsedXmlTree">
            <ng-container *ngTemplateOutlet="xmlNodeTpl; context: { $implicit: parsedXmlTree, parentPath: '' }"></ng-container>
          </div>
          <!-- Fallback: raw XML when tree parsing fails (e.g. malformed XML) -->
          <ng-container *ngIf="!parsedXmlTree">
            <div class="info-message" style="margin: 0.5rem 0;">
              <p>⚠️ XML could not be parsed into a tree view — showing raw content</p>
            </div>
            <div class="json-result-wrapper">
              <button class="copy-btn" (click)="copyResult()" title="Copy XML">📋</button>
              <pre class="json-result">{{ xmlContent }}</pre>
            </div>
          </ng-container>
        </div>

        <!-- Recursive XML tree node template -->
        <ng-template #xmlNodeTpl let-node let-parentPath="parentPath">
          <div class="tree-item">
            <!-- Value+confidence collapsed inline -->
            <ng-container *ngIf="xmlNodeAsValueConfidence(node) as xmlPair; else xmlNodeFull">
              <div class="tree-node-display xml-node">
                <span class="tree-toggle-spacer"></span>
                <span class="tree-key xml-tag">{{ node.tag }}</span>
                <!-- Editable XML collapsed value -->
                <ng-container *ngIf="editingPath === (parentPath ? parentPath + '.' : '') + node.tag + '.value'; else xmlPairDisplay">
                  <input class="tree-edit-input" [(ngModel)]="editingValue"
                         (blur)="commitEdit((parentPath ? parentPath + '.' : '') + node.tag + '.value', xmlPair.value)"
                         (keydown)="onEditKeydown($event, (parentPath ? parentPath + '.' : '') + node.tag + '.value', xmlPair.value)"
                         autofocus />
                </ng-container>
                <ng-template #xmlPairDisplay>
                  <span class="tree-value" (click)="startEdit((parentPath ? parentPath + '.' : '') + node.tag + '.value', xmlPair.value)" (mouseenter)="onValueHover(xmlPair.value)" (mouseleave)="onValueHoverOut()">{{ xmlPair.value }}</span>
                  <span *ngIf="xmlPair.confidence !== null"
                        [class]="confidenceClass(xmlPair.confidence)">{{ confidencePercent(xmlPair.confidence) }}</span>
                </ng-template>
              </div>
            </ng-container>
            <!-- Normal node rendering -->
            <ng-template #xmlNodeFull>
              <div class="tree-node-display xml-node" (click)="node._collapsed = !node._collapsed">
                <span *ngIf="node.children.length > 0" class="tree-toggle">{{ node._collapsed ? '+' : '−' }}</span>
                <span *ngIf="node.children.length === 0" class="tree-toggle-spacer"></span>
                <span class="tree-key xml-tag">{{ node.tag }}</span>
                <ng-container *ngIf="node.children.length === 0 && node.text">
                  <!-- Editable XML leaf text -->
                  <ng-container *ngIf="editingPath === (parentPath ? parentPath + '.' : '') + node.tag; else xmlLeafDisplay">
                    <input class="tree-edit-input" [(ngModel)]="editingValue"
                           (blur)="commitEdit((parentPath ? parentPath + '.' : '') + node.tag, node.text)"
                           (keydown)="onEditKeydown($event, (parentPath ? parentPath + '.' : '') + node.tag, node.text)"
                           (click)="$event.stopPropagation()"
                           autofocus />
                  </ng-container>
                  <ng-template #xmlLeafDisplay>
                    <span class="tree-value" (click)="$event.stopPropagation(); startEdit((parentPath ? parentPath + '.' : '') + node.tag, node.text)" (mouseenter)="$event.stopPropagation(); onValueHover(node.text)" (mouseleave)="onValueHoverOut()">{{ node.text }}</span>
                  </ng-template>
                </ng-container>
                <ng-container *ngIf="node.children.length > 0">
                  <span class="tree-type">{{ node.children.length }} element{{ node.children.length > 1 ? 's' : '' }}</span>
                </ng-container>
                <ng-container *ngIf="node.attributes && getObjectKeys(node.attributes).length > 0">
                  <span class="tree-attr" *ngFor="let attr of getObjectKeys(node.attributes)">
                    {{ attr }}="{{ node.attributes[attr] }}"
                  </span>
                </ng-container>
              </div>
              <div *ngIf="node.children.length > 0 && !node._collapsed" class="tree-children">
                <ng-container *ngFor="let child of node.children">
                  <ng-container *ngTemplateOutlet="xmlNodeTpl; context: { $implicit: child, parentPath: (parentPath ? parentPath + '.' : '') + node.tag }"></ng-container>
                </ng-container>
              </div>
            </ng-template>
          </div>
        </ng-template>

        <!-- JSON raw view -->
        <div *ngIf="viewMode === ViewMode.JSON && !isXmlResult && !isCsvResult" class="json-view">
          <div class="json-result-wrapper" [class.paywall-locked]="(auth.user?.balance ?? 0) < paywallMinBalance">
            <button class="copy-btn" (click)="copyResult()" title="Copy JSON">📋</button>
            <pre class="json-result">{{ effectiveData | json }}</pre>
          </div>
        </div>

        <!-- XML raw view -->
        <div *ngIf="viewMode !== ViewMode.TREE && isXmlResult && xmlContent" class="json-view">
          <div class="json-result-wrapper" [class.paywall-locked]="(auth.user?.balance ?? 0) < paywallMinBalance">
            <button class="copy-btn" (click)="copyResult()" title="Copy XML">📋</button>
            <pre class="json-result">{{ xmlContent }}</pre>
          </div>
        </div>

        <!-- CSV Spreadsheet View -->
        <div *ngIf="viewMode === ViewMode.SPREADSHEET && isCsvResult && csvTable" class="spreadsheet-view">
          <div class="spreadsheet-wrapper">
            <table class="spreadsheet-table">
              <thead>
                <tr>
                  <th class="row-index">#</th>
                  <th *ngFor="let h of csvTable.headers">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of csvTable.rows; let i = index">
                  <td class="row-index">{{ i + 1 }}</td>
                  <td *ngFor="let cell of row" class="spreadsheet-cell">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- CSV Text View -->
        <div *ngIf="viewMode === ViewMode.JSON && isCsvResult" class="json-view">
          <div class="json-result-wrapper" [class.paywall-locked]="(auth.user?.balance ?? 0) < paywallMinBalance">
            <button class="copy-btn" (click)="copyResult()" title="Copy CSV">📋</button>
            <pre class="json-result">{{ csvContent }}</pre>
          </div>
        </div>

        <!-- XSD validation status -->
        <ng-container *ngIf="isXmlResult && xmlContent">
          <div *ngIf="xmlValid === true" class="success-message">
            <p>✅ Valid according to schema definition</p>
          </div>
          <div *ngIf="xmlValid === false" class="info-message">
            <p>⚠️ XML has validation warnings —
              <a href="javascript:void(0)" class="validation-link"
                 (click)="showValidationReport(xmlErrors)">Validation Report</a>
            </p>
          </div>
          <div *ngIf="xmlValid === null" class="info-message">
            <p>ℹ️ No XSD configured — validation skipped</p>
          </div>
        </ng-container>

        <!-- Table format raw view (text formats: csv, odf) -->
        <div *ngIf="isTableResult && tableContent && !isBinaryTableResult" class="json-view">
          <div class="json-result-wrapper">
            <button class="copy-btn" (click)="copyResult()"
                    [title]="'Copy ' + (tableFormat | uppercase)">📋</button>
            <pre class="json-result">{{ tableContent }}</pre>
          </div>
        </div>

        <!-- Table format binary view (xlsx): not human-readable, offer download -->
        <div *ngIf="isTableResult && tableContent && isBinaryTableResult" class="json-view">
          <div class="binary-download-hint">
            <p>{{ 'extractionReview.binaryFormatHint' | translate:{ format: (tableFormat | uppercase) } }}</p>
            <button class="btn-toggle active" (click)="downloadResult()">⬇ {{ tableFormat | uppercase }}</button>
          </div>
        </div>

      </ng-container>
    </div>

    <!-- RIGHT: Page images (resizable) -->
    <div class="panel-right" [style.width.px]="imagePanelWidth">
      <div class="panel-resize-handle" (mousedown)="onResizeStart($event)"></div>
      <div class="panel-header">
        {{ (isNativeMessage ? 'extractionReview.nativeMessage' : 'extractionReview.pages') | translate }}
        <span class="no-match-indicator"
              *ngIf="!isNativeMessage && highlightWord && highlightMatchCount === 0"
              [title]="'extractionReview.highlightNotFound' | translate">⊘</span>
      </div>

      <!-- Native SWIFT MT / ISO 20022: no page images exist (page_count = 0),
           so the pane shows the original message text instead. -->
      <div class="images-scroll" *ngIf="isNativeMessage">
        <pre class="native-raw">{{ nativeRaw }}</pre>
        <div class="no-result" *ngIf="!nativeRaw">
          {{ 'extractionReview.nativeRawUnavailable' | translate }}
        </div>
      </div>

      <div class="images-scroll" *ngIf="!isNativeMessage">
        <div class="page-image-wrapper" *ngFor="let page of pages">
          <div class="page-image-container">
            <img
              *ngIf="pageImageUrls[page.oid]"
              [src]="pageImageUrls[page.oid]"
              [alt]="'Page ' + page.page_number"
              class="page-image"
              [id]="'page-img-' + page.oid"
            />
            <canvas
              *ngIf="pageImageUrls[page.oid]"
              class="ocr-highlight-canvas"
              [id]="'ocr-canvas-' + page.oid"
            ></canvas>
          </div>
          <div class="image-placeholder" *ngIf="!pageImageUrls[page.oid]">
            {{ page.page_number }}
          </div>
        </div>
        <div class="no-pages" *ngIf="pages.length === 0">
          {{ 'extractionReview.pages' | translate }}
        </div>
      </div>
    </div>

  </div>

  <!-- Extraction billing info + feedback (below split layout) -->
  <div class="cost-estimate" *ngIf="isExtracted && extractionModel">
    <div class="extraction-feedback" *ngIf="isExtracted">
      <span>{{ 'extractionReview.feedbackPrompt' | translate }}</span>
      <button class="btn-feedback" [class.selected]="extractionFeedback === 'up'" (click)="sendExtractionFeedback('up')" title="Good extraction">👍</button>
      <button class="btn-feedback" [class.selected]="extractionFeedback === 'down'" (click)="sendExtractionFeedback('down')" title="Poor extraction">👎</button>
    </div>
    <span *ngIf="isDeveloper">{{ 'extractionReview.model' | translate }}: {{ displayModel(extractionModel) }}</span>
    <span *ngIf="billingTokens !== null">{{ billingTokens | localNumber:'1.2-2' }} ꝟ</span>
    <span *ngIf="isDeveloper && estimatedCost">{{ 'extractionReview.estimatedCost' | translate }}: {{ estimatedCost }}</span>
  </div>

  <!-- Paywall Modal -->
  <div class="paywall-overlay" *ngIf="showPaywall" (click)="showPaywall = false">
    <div class="paywall-modal" (click)="$event.stopPropagation()">
      <button class="paywall-close" (click)="showPaywall = false">&times;</button>

      <ng-container *ngIf="auth.user; else registerCta">
        <h3>{{ 'paywall.title' | translate }}</h3>
        <p>{{ 'paywall.text' | translate:{ required: paywallMinBalance } }}</p>
        <p class="paywall-balance">{{ 'paywall.balance' | translate }}: <strong>{{ (auth.user.balance ?? 0) | localNumber:'1.0-0' }} ꝟ</strong></p>
        <form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_top" class="paywall-form" (click)="$event.stopPropagation()">
          <input type="hidden" name="cmd" value="_s-xclick">
          <input type="hidden" name="hosted_button_id" value="{{paypalHostedButtonId}}">
          <input type="hidden" name="currency_code" value="EUR">
          <input type="hidden" name="custom" value="{{auth.user.email || ''}}">
          <input type="image" src="https://www.paypalobjects.com/webstatic/en_US/i/btn/png/silver-rect-paypal-34px.png" style="border:0;cursor:pointer;vertical-align:middle;" name="submit" title="PayPal" alt="PayPal">
        </form>
      </ng-container>

      <ng-template #registerCta>
        <h3>{{ 'paywall.registerTitle' | translate }}</h3>
        <p>{{ 'paywall.registerText' | translate }}</p>
        <div class="paywall-form">
          <button class="btn btn-primary" (click)="auth.register()">{{ 'app.register' | translate }}</button>
          <button class="btn" (click)="auth.login()">{{ 'app.login' | translate }}</button>
        </div>
      </ng-template>

      <button class="paywall-close-btn" (click)="showPaywall = false">{{ 'paywall.close' | translate }}</button>
    </div>
  </div>

  <app-signature-panel
    [title]="signatureDialogTitle"
    [result]="signatureDialogResult"
    [dossierId]="dossierId"
    [uploadId]="uploadId"
    (rechecked)="onSignatureRechecked($event)"
    (close)="closeSignatureDialog()"
  ></app-signature-panel>

</div>
